import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Lock, Trash2, Plus, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminAction } from "@/lib/adminApi";
import { successToast, errorToast } from "@/components/ui/sonner";
import {
  AdminPageHeader,
  AdminPageSkeleton,
  AdminSection,
  AdminTable,
  AdminSearch,
  AdminPill,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from "./AdminUI";

const PROTECTED_EMAIL = "dean@sorvaine.com";

interface AdminRow {
  user_id: string;
  email?: string;
  name?: string;
  created_at: string;
}

export function AdminTeamTab() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [supportEmail, setSupportEmail] = useState("");

  const { data: admins, isLoading } = useQuery({
    queryKey: ["admin", "admins"],
    queryFn: () => adminAction("get_admins"),
  });

  const { data: supportAdmins, isLoading: supportLoading } = useQuery({
    queryKey: ["admin", "support-admins"],
    queryFn: () => adminAction("get_support_admins"),
  });

  const grant = useMutation({
    mutationFn: () => adminAction("grant_admin", { target_email: email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "admins"] });
      successToast("Admin granted", `${email} now has super admin access.`);
      setEmail("");
    },
    onError: (e: Error) => errorToast("Error", e.message),
  });

  const revoke = useMutation({
    mutationFn: (userId: string) => adminAction("revoke_admin", { target_user_id: userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "admins"] });
      successToast("Access revoked", "Super admin access has been removed.");
    },
    onError: (e: Error) => errorToast("Error", e.message),
  });

  const grantSupport = useMutation({
    mutationFn: () => adminAction("grant_support_admin", { target_email: supportEmail }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "support-admins"] });
      successToast("Support admin granted", `${supportEmail} now has limited admin access.`);
      setSupportEmail("");
    },
    onError: (e: Error) => errorToast("Error", e.message),
  });

  const revokeSupport = useMutation({
    mutationFn: (userId: string) => adminAction("revoke_support_admin", { target_user_id: userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "support-admins"] });
      successToast("Access revoked", "Support admin access has been removed.");
    },
    onError: (e: Error) => errorToast("Error", e.message),
  });

  if (isLoading) return <AdminPageSkeleton stats={0} rows={4} columns={3} />;

  const list: AdminRow[] = admins || [];
  const supportList: AdminRow[] = supportAdmins || [];

  return (
    <div className="space-y-10">
      <AdminPageHeader
        eyebrow="System"
        title="Platform team"
        description="The people with admin access to Sorvaine. Every action they take is logged to the audit trail."
        meta={`${list.length} super · ${supportList.length} support`}
      />

      <div
        className="flex items-start gap-3 rounded-[4px] px-5 py-4"
        style={{
          background: "rgba(217,119,6,0.06)",
          border: "1px solid rgba(217,119,6,0.18)",
        }}
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-amber-700">
            Privileged access
          </p>
          <p className="text-[12.5px] text-muted-foreground mt-1 leading-relaxed max-w-2xl">
            Super admins can view every organisation, impersonate any user, and modify
            platform-wide settings. Grant access sparingly.
          </p>
        </div>
      </div>

      <AdminSection
        title="Grant super admin"
        description="Adds full super admin privileges to an existing Sorvaine account."
      >
        <div className="flex gap-2 max-w-xl">
          <AdminSearch
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (!email.trim()) {
                  errorToast("Please enter an email address.");
                  return;
                }
                grant.mutate();
              }
            }}
            placeholder="Email address…"
            containerClassName="flex-1"
          />
          <Button
            onClick={() => grant.mutate()}
            disabled={grant.isPending || !email.trim()}
            className="h-9 px-4 text-[11.5px] uppercase tracking-[0.18em] rounded-[3px]"
            style={{ background: "#c2541a" }}
          >
            {grant.isPending ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Plus className="w-3.5 h-3.5 mr-1.5" />
            )}
            Grant
          </Button>
        </div>
      </AdminSection>

      <AdminSection title="Current super admins">
        <AdminTable
          head={
            <>
              <th className={adminTableHeadCellClass}>Admin</th>
              <th className={adminTableHeadCellClass}>Granted</th>
              <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}></th>
            </>
          }
        >
          {list.map((a, i) => {
            const isProtected = a.email === PROTECTED_EMAIL;
            return (
              <tr
                key={a.user_id}
                className={adminTableRowClass}
                style={{ borderBottom: i === list.length - 1 ? "none" : "1px solid rgba(58,53,48,0.06)" }}
              >
                <td className={adminTableCellClass}>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: "rgba(194,84,26,0.08)",
                        border: "1px solid rgba(194,84,26,0.14)",
                      }}
                    >
                      <span
                        className="text-[12px] text-foreground"
                        style={{ fontFamily: "Outfit, sans-serif", fontWeight: 400 }}
                      >
                        {(a.name || a.email || "?")[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13.5px] text-foreground">{a.name || "—"}</div>
                      <div className="text-[11.5px] text-muted-foreground">{a.email}</div>
                    </div>
                  </div>
                </td>
                <td className={`${adminTableCellClass} text-muted-foreground tabular-nums`}>
                  {new Date(a.created_at).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </td>
                <td className={`${adminTableCellClass} text-right`}>
                  {isProtected ? (
                    <AdminPill tone="accent">
                      <Lock className="w-2.5 h-2.5 mr-1" /> Protected
                    </AdminPill>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => revoke.mutate(a.user_id)}
                      disabled={revoke.isPending}
                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 text-[11px] uppercase tracking-[0.16em] h-7"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Revoke
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </AdminTable>
      </AdminSection>

      <div className="editorial-divider pt-8" />

      <AdminSection
        title="Support admins"
        description="Limited admin access. Can view dashboards, users, orgs, subscriptions, churn, audit and analytics. Cannot impersonate, change platform settings, manage feature flags, edit the team, or touch the embed queue."
      >
        <div className="flex gap-2 max-w-xl mb-6">
          <AdminSearch
            type="email"
            value={supportEmail}
            onChange={(e) => setSupportEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (!supportEmail.trim()) {
                  errorToast("Please enter an email address.");
                  return;
                }
                grantSupport.mutate();
              }
            }}
            placeholder="Email address…"
            containerClassName="flex-1"
          />
          <Button
            onClick={() => grantSupport.mutate()}
            disabled={grantSupport.isPending || !supportEmail.trim()}
            variant="outline"
            className="h-9 px-4 text-[11.5px] uppercase tracking-[0.18em] rounded-[3px]"
          >
            {grantSupport.isPending ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Plus className="w-3.5 h-3.5 mr-1.5" />
            )}
            Promote
          </Button>
        </div>

        {supportLoading ? (
          <div className="text-[12px] text-muted-foreground">Loading…</div>
        ) : supportList.length === 0 ? (
          <div className="text-[12.5px] text-muted-foreground italic">
            No support admins yet. Promote a user above to grant limited access.
          </div>
        ) : (
          <AdminTable
            head={
              <>
                <th className={adminTableHeadCellClass}>Support admin</th>
                <th className={adminTableHeadCellClass}>Granted</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: "right" }}></th>
              </>
            }
          >
            {supportList.map((a, i) => (
              <tr
                key={a.user_id}
                className={adminTableRowClass}
                style={{ borderBottom: i === supportList.length - 1 ? "none" : "1px solid rgba(58,53,48,0.06)" }}
              >
                <td className={adminTableCellClass}>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: "rgba(58,53,48,0.05)",
                        border: "1px solid rgba(58,53,48,0.10)",
                      }}
                    >
                      <span
                        className="text-[12px] text-foreground"
                        style={{ fontFamily: "Outfit, sans-serif", fontWeight: 400 }}
                      >
                        {(a.name || a.email || "?")[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13.5px] text-foreground">{a.name || "—"}</div>
                      <div className="text-[11.5px] text-muted-foreground">{a.email}</div>
                    </div>
                  </div>
                </td>
                <td className={`${adminTableCellClass} text-muted-foreground tabular-nums`}>
                  {new Date(a.created_at).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </td>
                <td className={`${adminTableCellClass} text-right`}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => revokeSupport.mutate(a.user_id)}
                    disabled={revokeSupport.isPending}
                    className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 text-[11px] uppercase tracking-[0.16em] h-7"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </AdminTable>
        )}
      </AdminSection>
    </div>
  );
}
